import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE } from "@/lib/api";
import { getAccessToken } from "@/lib/safeStorage";
import { BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * One-tap "View user manual" button.
 *
 * Behaviour:
 *  - Visible only to logged-in users (returns null otherwise).
 *  - Opens `GET /api/manuals?lang=<current-lang>&auth=<token>` in a new tab.
 *  - We use a direct URL (token in query string, like the existing
 *    /api/files/{path}?auth=... pattern) so the call is SYNCHRONOUS from the
 *    user click — no popup blockers, no race conditions, no headless-Chrome
 *    quirks with blob: URL navigation. The backend serves the PDF inline
 *    (Content-Disposition: inline) so the browser's built-in viewer renders
 *    it and exposes Download / Print / Zoom controls.
 *  - Role is auto-detected from the JWT, so one button covers admin /
 *    sales / partner / simple_user dashboards.
 */
export default function ManualButton({ className = "", variant = "icon" }) {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  // We have nothing async to await, but keep a short loading flicker so the
  // user sees feedback if the new tab is slow to open.
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const handleOpen = () => {
    const token = getAccessToken();
    const lang = i18n.language?.startsWith("en") ? "en" : "ro";
    // Build the URL with the token in the query string when we have one.
    // If localStorage is empty (e.g. privacy mode, cleared cache) the
    // browser will still authenticate via the httpOnly `access_token` cookie
    // set by /api/auth/login — both frontend and backend live on the same
    // origin so the cookie is sent automatically on anchor navigation.
    const url = token
      ? `${API_BASE}/manuals?lang=${lang}&auth=${encodeURIComponent(token)}`
      : `${API_BASE}/manuals?lang=${lang}`;

    // Trigger a synthetic anchor click instead of window.open. Two reasons:
    //   1. window.open(..., "noopener") returns null in modern browsers as a
    //      security guarantee, which made the fallback path think the popup
    //      was blocked and navigate the *current* tab to the PDF — leaving
    //      the user with the manual open in BOTH tabs.
    //   2. An anchor with `target="_blank" rel="noopener noreferrer"` opens
    //      a fresh tab AND severs the opener link, without any return-value
    //      weirdness. This is the canonical "open in new tab" pattern.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();

    setLoading(true);
    toast.success(t("manual.toast_opened"));
    setTimeout(() => setLoading(false), 1200);
  };

  // Icon-only variant (default — used inside the navbar)
  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className={`hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 bg-white/5 text-zinc-300 hover:text-orange-400 hover:border-orange-500/40 hover:bg-orange-500/10 transition disabled:opacity-50 ${className}`}
        title={t("manual.button_title")}
        aria-label={t("manual.button_title")}
        data-testid="manual-button"
      >
        {loading ? (
          <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
        ) : (
          <BookOpen size={16} strokeWidth={1.5} />
        )}
      </button>
    );
  }

  // Full-button variant (kept for future inline placement on cards/dashboards)
  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={loading}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/10 bg-white/5 text-sm text-zinc-200 hover:text-orange-400 hover:border-orange-500/40 hover:bg-orange-500/10 transition disabled:opacity-50 ${className}`}
      data-testid="manual-button"
    >
      {loading ? (
        <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
      ) : (
        <BookOpen size={14} strokeWidth={1.5} />
      )}
      <span>{t("manual.button_label")}</span>
    </button>
  );
}
